import { useRef } from 'react';
import Logo from '../Logo';
import { User } from '@v2/shared/lib/types';
import { faUserCircle } from '@fortawesome/pro-light-svg-icons';
import { MenuItem, Category, SearchBarState, ToggleState } from '@v2/shared/lib/types';
import { SearchAutoCompleteProps } from '@v2/shared/lib/useSearchAutoComplete';

import SearchForm from '@v2/shared/components/SearchForm';
import { DesktopNavIconButton, DesktopSupportPopover, DesktopCategoryBar } from '.';
import { CartLink } from '../CartLink/CartLink';
import { cn } from '@v2/shared/lib/utils';

type Props = {
  country: string;
  categories: Category[];
  booksCategories?: Category[];
  accountMenuState: ToggleState;
  /** Null while the cart has not loaded. */
  cartCount: number | null;
  user: User | null | undefined;
  homeUrl: string;
  cartUrl: string;
  specialDealLinks: MenuItem[];
  searchBarState: SearchBarState;
  searchAutoCompleteProps: SearchAutoCompleteProps;
};

export default function DesktopNav({
  country,
  categories,
  booksCategories,
  accountMenuState,
  cartCount,
  user,
  homeUrl,
  cartUrl,
  specialDealLinks,
  searchAutoCompleteProps,
  searchBarState,
}: Props) {
  const searchInputRef = useRef(null);

  return (
    <nav
      aria-label="Primary"
      className={cn(
        'bg-white hidden md:flex flex-col md:items-start xl:items-center justify-center border-b-[1.5px] border-slate-200',
        'h-[126px] xl:h-[82px]'
      )}
    >
      <div className="flex items-center md:px-6 2xl:px-10 w-full md:h-[70px]">
        <div className="flex flex-grow items-center">
          <a
            href={homeUrl}
            className="cursor-pointer items-center hidden md:flex mb-[5px] px-2 py-2"
            aria-label="Navigate home"
          >
            <Logo country={country} size="sm" />
          </a>
          <div className="hidden xl:flex flex-grow justify-center gap-1">
            <DesktopCategoryBar
              categories={categories}
              specialDealLinks={specialDealLinks}
              booksCategories={booksCategories}
            />
          </div>
          <div className="flex flex-grow lg:justify-between xl:justify-end items-center">
            <div className="flex w-full group relative mx-5 lg:ml-5 lg:mr-7 xl:max-w-sm">
              <SearchForm
                id="global-search-desktop"
                searchBarState={searchBarState}
                ref={searchInputRef}
                searchAutoCompleteProps={searchAutoCompleteProps}
              />
            </div>
            <div className="flex items-center gap-1">
              <DesktopSupportPopover />
              <DesktopNavIconButton
                icon={faUserCircle}
                onClick={accountMenuState.toggle}
                title={user && user.name.first_name ? `Hi, ${user.name.first_name}` : 'Account'}
              />
              <CartLink cartCount={cartCount} cartUrl={cartUrl} />
            </div>
          </div>
        </div>
      </div>
      <div
        className={cn(
          'hidden xl:hidden',
          'items-center justify-center border-slate-200',
          'md:flex md:w-full md:h-[56px] md:border-t-[1.5px]'
        )}
      >
        <DesktopCategoryBar
          categories={categories}
          specialDealLinks={specialDealLinks}
          booksCategories={booksCategories}
        />
      </div>
    </nav>
  );
}
